import { Suspense, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'

import { usePublicPage } from '@/shared/auth/public-page'
import { RequireAuth } from '@/shared/auth/RequireAuth'
import type { RegisteredRoute } from '@/shared/registry'
import { cn, focusRing } from '@/shared/ui/cn'

import { PageFallback } from './AppShell'
import { ErrorBoundary, reloadPage } from './ErrorBoundary'
import { ErrorState } from './ErrorState'
import { RouteTitle } from './page-title'

/**
 * Shown in place of a page whose chunk failed to load or that failed to render. It has the page's
 * `h1`, so focus lands on it after navigating, and an alert that is announced.
 */
function PageError() {
  const intl = useIntl()

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6 lg:py-10">
      <h1
        tabIndex={-1}
        className={cn(
          'mb-6 font-heading text-2xl font-medium leading-[1.2] text-text-primary',
          focusRing,
        )}
      >
        {intl.formatMessage({ id: 'shell.pageError.title' })}
      </h1>
      <ErrorState title={intl.formatMessage({ id: 'shell.pageError.body' })} onRetry={reloadPage} />
    </div>
  )
}

/** Keeps a page's failure, such as its chunk failing to load, inside the page. */
export function PageBoundary(props: { readonly children: ReactNode }) {
  // A new address tries again, so one failed page never hides the next.
  const { pathname } = useLocation()
  return (
    <ErrorBoundary fallback={<PageError />} resetKey={pathname}>
      {props.children}
    </ErrorBoundary>
  )
}

/**
 * One registered route's element: its title, the sign-in guard unless the route is public, and
 * its page, loaded from its own chunk once the guard lets it through. A failure stays inside it.
 */
export function RoutePage(props: { readonly route: RegisteredRoute }) {
  const { Page, titleId } = props.route
  usePublicPage(props.route.public === true)
  const page = (
    <PageBoundary>
      <Suspense fallback={<PageFallback />}>
        <Page />
      </Suspense>
    </PageBoundary>
  )

  return (
    <RouteTitle titleId={titleId}>
      {props.route.public === true ? page : <RequireAuth>{page}</RequireAuth>}
    </RouteTitle>
  )
}
